// ── BCHNostr — Sponsored Business Card ────────────────────────────
//
// Renders a VERIFIED business's paid promotion as a clearly-labeled card in
// the feed. Mirrors the visual language of BoostedCampaignCard (gold accent
// + slim "paid" label) and, like every paid surface in the app, is honest:
// the label says "Sponsored" and the tooltip says it's a paid placement, not
// an organic recommendation.
//
// All rendered strings/URLs are re-sanitized on read — the event is signed,
// which proves authorship, not safety (see the app security model).

import { Link } from 'react-router-dom';
import { BadgeCheck, Megaphone, Store, MapPin, Globe, ExternalLink } from 'lucide-react';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useBusinessProfile } from '../hooks/useBusinessProfile';
import { categoryLabel, professionalTypeLabel } from '../lib/business';
import { safeUrl } from '../lib/podcast';
import type { ActiveBusinessAd } from '../hooks/useBusinessAds';

export default function SponsoredBusinessCard({ ad }: { ad: ActiveBusinessAd }) {
  const pubkey = ad.businessPubkey;
  const { profile } = useNostrProfile(pubkey);
  const { business } = useBusinessProfile(pubkey);

  // Prefer the business listing's own name; fall back to kind-0 metadata.
  const name =
    business?.name ||
    profile?.display_name ||
    profile?.name ||
    pubkey.slice(0, 8) + '…';

  // The pitch: the ad's optional headline wins, else the business tagline,
  // else a trimmed description. All already sanitized by their parsers.
  const pitch =
    ad.data.headline ||
    business?.tagline ||
    business?.description ||
    '';

  const picture = safeUrl(profile?.picture);
  const website = safeUrl(business?.website);
  const location = business?.onlineOnly ? 'Online' : business?.location || '';
  const typeLabel = professionalTypeLabel(business?.proType);
  const catLabel = business?.category ? categoryLabel(business.category) : '';

  return (
    <div className="relative border-l-[3px] border-boost-gold">
      {/* Slim honest label */}
      <div className="flex items-center gap-1.5 px-4 pt-2">
        <Megaphone className="w-3 h-3 text-boost-gold-ink shrink-0" />
        <span
          className="text-[11px] text-boost-gold-ink font-bold tracking-wide"
          title="A paid promotion from a verified business — not an organic recommendation."
        >
          Sponsored
        </span>
      </div>

      <Link
        to={`/profile/${pubkey}`}
        className="block px-4 py-3 hover:bg-muted/30 transition-colors"
      >
        <div className="flex gap-3">
          {/* Avatar */}
          <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
            {picture ? (
              <img
                src={picture}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
              />
            ) : (
              <Store className="w-5 h-5 text-primary" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="font-bold text-sm leading-snug truncate">{name}</h3>
              {/* Verified — this card only ever shows for verified businesses. */}
              <BadgeCheck className="w-4 h-4 text-emerald-500 shrink-0" />
            </div>

            {/* Type · category · location line */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground mt-0.5">
              <span className="font-medium">{typeLabel}</span>
              {catLabel && <span>· {catLabel}</span>}
              {location && (
                <span className="inline-flex items-center gap-0.5">
                  · <MapPin className="w-3 h-3" /> {location}
                </span>
              )}
            </div>

            {pitch && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1.5">{pitch}</p>
            )}
          </div>
        </div>
      </Link>

      {/* CTA footer — view the business, or its website (opens in new tab). */}
      <div className="flex items-center gap-2 mx-3 mb-3">
        <Link
          to={`/profile/${pubkey}`}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-primary to-emerald-500 text-white text-xs font-bold hover:opacity-90 transition-opacity"
        >
          <Store className="w-3.5 h-3.5" />
          View Business
        </Link>
        {website && (
          <a
            href={website}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="no-navigate flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-border text-xs font-semibold hover:border-primary/40 hover:text-primary transition-colors"
          >
            <Globe className="w-3.5 h-3.5" /> Website <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
}
